import React from 'react';
import { Link } from 'react-router-dom';
import '../CSS/Company.css';
import '../CSS/CompanyPage.css';
import Reveal from './Reveal';
import CountUp from './CountUp';

const pad = (n: number) => `0${n}`.slice(-2);
const delay = (i: number) => ({ ['--i' as string]: i } as React.CSSProperties);

const FLOW = ['Ideation', 'Design', 'Deployment', 'Beyond'];

const OFFERINGS = [
  { title: 'AI Agents & Intelligence', text: 'Agents, agentic orchestrators, generative AI and data decision pipelines.', link: '/aiml/', cta: 'Explore AI services' },
  { title: 'Data & Software Engineering', text: 'Governed data foundations and cloud-native, API-first software.', link: '/softwareengineering/', cta: 'Explore engineering' },
  { title: 'The Catalyst Suite', text: 'Our decision-support products for research, academic and workforce leaders.', link: '/products/', cta: 'Explore the products' },
];

const VALUES = [
  { title: 'Collaborative Innovation', text: 'We co-create with clients, where curiosity and strategy flourish.' },
  { title: 'Cross-Functional Expertise', text: 'Data science, AI, digital strategy, healthcare, cybersecurity and more, working as one.' },
  { title: 'Proven Technical Process', text: '175+ software products built over 15+ years of custom development.' },
  { title: 'Domain-Driven Approach', text: 'Generative AI, ML and data engineering, adapted to each client, from model to deployment.' },
  { title: 'Agile & Scalable Delivery', text: 'Agile methods, CI/CD, automated QA and cloud-native stacks for fast-moving needs.' },
];

const STATS = [
  { value: '1,200+', label: 'Projects completed' },
  { value: '1,800+', label: 'Design resources' },
  { value: '100%', label: 'Client satisfaction rate' },
];

const Company: React.FC = () => {
  return (
    <div className="company-page co-page">
      {/* Banner */}
      <div className="sv_banner" style={{ backgroundImage: "url('/Assets/Company/company-banner-image.png')" }}>
        <div className="sv-banner-overlay"></div>
        <div className="sv-banner-content">
          <h1 className="sv-banner-title">Company Overview</h1>
          <p className="sv-banner-subtitle">AI and software engineering, built to run your operations.</p>
        </div>
      </div>

      {/* Who we are */}
      <div className="sv-section sv-intro">
        <Reveal className="sv-container">
          <p className="sv-intro-text">
            Zemantics LLC is an AI and software engineering company based in Princeton, New Jersey.
          </p>
          <p className="co-lead">
            We build AI agents, agentic orchestrators and data decision pipelines for enterprises across industries, from ideation through deployment and beyond.
          </p>
          <div className="sv-flow" aria-hidden="true" style={{ ['--n' as string]: FLOW.length } as React.CSSProperties}>
            {FLOW.map((label, i) => (
              <React.Fragment key={label}>
                {i > 0 && <span className="sv-line" style={delay(i)}></span>}
                <span className={`sv-node${i === FLOW.length - 1 ? ' sv-node-last' : ''}`} style={delay(i)}>
                  {label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </Reveal>
      </div>

      {/* What we do */}
      <div className="sv-section sv-tint">
        <Reveal className="sv-container">
          <h2 className="sv-title">What We Do</h2>
          <div className="sv-grid">
            {OFFERINGS.map((item, i) => (
              <div className="sv-card" key={item.title} style={delay(i)}>
                <span className="sv-card-number">{pad(i + 1)}</span>
                <h3 className="sv-card-title">{item.title}</h3>
                <p className="sv-card-text">{item.text}</p>
                <Link to={item.link} className="co-card-link">{item.cta}</Link>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Mission */}
      <div className="sv-section co-mission">
        <Reveal className="sv-container">
          <h2 className="sv-title">Our Mission</h2>
          <p className="co-mission-text">
            To <strong>demystify and democratize AI</strong>: a trusted guide from first concept to full-scale deployment, so you can embrace AI with clarity and confidence.
          </p>
        </Reveal>
      </div>

      {/* Team & leadership */}
      <div className="sv-section sv-tint">
        <Reveal className="sv-container">
          <div className="co-team">
            <img
              className="co-team-image"
              src="/Assets/Company/team-leadership-banner-image.png"
              alt="Zemantics team and leadership"
              loading="lazy"
              decoding="async"
            />
            <div className="co-team-text">
              <h2 className="co-team-title">Team &amp; Leadership</h2>
              <p className="co-team-number">
                <span>250+</span> years
              </p>
              <p className="co-team-caption">
                of combined leadership experience across strategy, AI and technology, delivering innovative solutions globally.
              </p>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Values */}
      <div className="sv-section">
        <Reveal className="sv-container">
          <h2 className="sv-title">Values &amp; Differentiators</h2>
          <div className="co-values">
            {VALUES.map((value, i) => (
              <div className="sv-card" key={value.title} style={delay(i)}>
                <span className="sv-card-number">{pad(i + 1)}</span>
                <h3 className="sv-card-title">{value.title}</h3>
                <p className="sv-card-text">{value.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Achievements */}
      <div className="co-achievements">
        <Reveal className="sv-container">
          <h2 className="co-achievements-title">Key Partnerships &amp; Achievements</h2>
          <p className="co-achievements-text">
            Through <strong>Zemantics Ventures</strong>, we partner with enterprises across AI, data science, executive search, digital strategy, healthcare, cybersecurity, hospitality and more.
          </p>
          <div className="co-stats">
            {STATS.map((stat) => (
              <div className="co-stat" key={stat.label}>
                <div className="co-stat-number">
                  <CountUp value={stat.value} />
                </div>
                <div className="co-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      {/* Call to action */}
      <div className="sv-cta">
        <div className="sv-cta-content">
          <h2 className="sv-cta-title">Let&apos;s build what&apos;s next.</h2>
          <Link to="/contact/" className="sv-btn">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Company;
